import { useState } from 'react';
import { downloadFilesAsZip } from '@tools/ui/api';
import { openToast } from '@tools/ui-utils/toast.util';
import useT from './useT';

export const MAX_DOWNLOAD_FILES = 15;

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const extractFailedFiles = async (error: unknown): Promise<string[]> => {
  try {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    const text = data instanceof Blob ? await data.text() : undefined;
    const parsed = text ? JSON.parse(text) : undefined;
    return Array.isArray(parsed?.failedFiles) ? parsed.failedFiles : [];
  } catch {
    return [];
  }
};

export const useDownloadFilesAsZip = () => {
  const { t } = useT();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async (fileIds: number[], partnerName: string | null) => {
    if (fileIds.length > MAX_DOWNLOAD_FILES) {
      openToast({
        type: 'error',
        message: t('files.downloadTooMany', { max: MAX_DOWNLOAD_FILES })
      });
      return;
    }

    setIsDownloading(true);
    try {
      const blob = await downloadFilesAsZip(fileIds, partnerName);
      const date = new Date().toISOString().slice(0, 10);
      saveBlob(blob, `attachments-${date}.zip`);
    } catch (error) {
      const failedFiles = await extractFailedFiles(error);
      openToast({
        type: 'error',
        message: failedFiles.length
          ? t('files.downloadFailed', { files: failedFiles.join(', ') })
          : t('files.downloadFailedGeneric')
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading };
};
